import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { ContactDataGroup } from "../SharedComponents/Interfaces/CustomContact.interfaces";
import { IContactData } from "../SharedComponents/Interfaces/CustomContact.interfaces";
import { transformContactListResponse } from "./ExploreCustomers.slice";
import {
  ICatalystBowlers,
  ICatalystCustomerDetails,
  ICatalystEngagementStatus,
  ICatalystActionItemProgress,
  ICatalystTelemetryVsSentimentGraph,
  ICatalystCustomerSentiment,
  ICatalystInternalCxECollaborators,
  ICatalystCustomerActionItems,
  ICatalystCompleteInsights,
  ICatalystEngagedPartners,
  ICatalystSentimentTrend
} from "../interfaces/Catalyst.interfaces";

export const catalystAPI = createApi({
  reducerPath: "catalyst",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getCatalystBowlers: builder.query<ICatalystBowlers, void>({
      query: () => "catalyst/getCatalystBowlers"
    }),
    getCatalystCustomerDetails: builder.query<ICatalystCustomerDetails[], void>({
      query: () => "catalyst/getCatalystCustomerDetails",
      transformResponse: (response: ICatalystCustomerDetails[]) => {
        return response.map((item) => {
          const withLegacyKeys = item as ICatalystCustomerDetails & { CRMLink?: string; crmlink?: string };
          return {
            ...item,
            crmLink: item.crmLink ?? withLegacyKeys.CRMLink ?? withLegacyKeys.crmlink,
          };
        });
      }
    }),
    getCatalystEngagementStatus: builder.query<ICatalystEngagementStatus[], void>({
      query: () => "catalyst/getCatalystEngagementStatus"
    }),
    getCatalystActionItemProgress: builder.query<ICatalystActionItemProgress[], void>({
      query: () => "catalyst/getCatalystActionItemProgress"
    }),
    getCatalystTelemetryVsSentimentGraph: builder.query<ICatalystTelemetryVsSentimentGraph[], void>({
      query: () => "catalyst/getCatalystTelemetryVsSentimentGraph"
    }),
    getCatalystCustomerSentiment: builder.query<ICatalystCustomerSentiment[], void>({
      query: () => "catalyst/getCatalystCustomerSentiment"
    }),
    getCatalystInternalCxECollaborators: builder.query<ContactDataGroup[], string>({
      query: (tenantId) => `catalyst/getCatalystInternalCxECollaborators?tenantId=${encodeURIComponent(tenantId)}`,
      transformResponse: async (response: ICatalystInternalCxECollaborators[]) => {
        const mapped: IContactData[] = response.map((c) => ({
          fullName: c.contact ?? "Unknown",
          emailAddress: c.companyEmail ?? "",
          nameRole: c.roleName ?? "",
          title: "",
          productName: c.roleName ?? "",
        }));
        return transformContactListResponse(mapped, "productName");
      }
    }),
    getCatalystCustomerActionItems: builder.query<ICatalystCustomerActionItems[], void>({
      query: () => "catalyst/getCatalystCustomerActionItems"
    }),
    getCatalystCompleteInsights: builder.query<ICatalystCompleteInsights[], void>({
      query: () => "catalyst/getCatalystCompleteInsights"
    }),
    getCatalystEngagedPartners: builder.query<ICatalystEngagedPartners[], void>({
      query: () => "catalyst/getCatalystEngagedPartners"
    }),
    getCatalystSentimentTrend: builder.query<ICatalystSentimentTrend[], void>({
      query: () => "catalyst/getCatalystSentimentTrend"
    })
  })
});

export const {
  useGetCatalystBowlersQuery,
  useGetCatalystCustomerDetailsQuery,
  useGetCatalystEngagementStatusQuery,
  useGetCatalystActionItemProgressQuery,
  useGetCatalystTelemetryVsSentimentGraphQuery,
  useGetCatalystCustomerSentimentQuery,
  useGetCatalystInternalCxECollaboratorsQuery,
  useGetCatalystCustomerActionItemsQuery,
  useGetCatalystCompleteInsightsQuery,
  useGetCatalystEngagedPartnersQuery,
  useGetCatalystSentimentTrendQuery
} = catalystAPI;
